import { action, makeObservable, observable } from "mobx";
import { DefaultRequestStatus, RequestStatus } from "../../shared/api/RequestStatus";
import inMeetingAppService, {
    InMeetingAppService,
    MeetingParticipant,
    MeetingType,
    MeetingTypeDictionaryEntry,
    UserStatus,
} from "../service/InMeetingAppService";
import { ParticipantDataFromResponse } from "../service/ParticipantDataFromResponse";
import { UserStatusFromResponse } from "../service/UserStatusFromResponse";
import { MeetingTypeDictionaryEntryFromResponse } from "../service/MeetingTypeDictionaryEntryFromResponse";
import { msTeamsMeetingRolesEnum } from "../enum/msTeamsMeetingRolesEnum";

export interface InMeetingAppStore {
    participantData: MeetingParticipant;
    userStatus: UserStatus;
    meetingTypeDictionary: MeetingTypeDictionaryEntry[];
    getParticipantData: (meetingId: string) => void;
    getUserStatus: (meetingId: string) => void;
    setMeetingType: (meetingId: string, meetingType: MeetingType) => void;
    getMeetingTypeDictionary: (language: string) => void;
}

export class DefaultInMeetingAppStore implements InMeetingAppStore {
    constructor(
        private service: InMeetingAppService,
        private getParticipantDataReqStatus: RequestStatus,
        private getUserStatusReqStatus: RequestStatus,
        private setMeetingTypeReqStatus: RequestStatus,
        private getMeetingTypeDictionaryReqStatus: RequestStatus
    ) {
        makeObservable(this, {
            getParticipantData: action,
            getUserStatus: action,
            getMeetingTypeDictionary: action,
            participantData: observable,
            userStatus: observable,
            meetingTypeDictionary: observable,
        });
    }

    public participantData: MeetingParticipant = { role: msTeamsMeetingRolesEnum.none };
    public userStatus: UserStatus = {} as UserStatus;
    public meetingTypeDictionary: MeetingTypeDictionaryEntry[] = [];

    public get getParticipantDataRequestStatus() {
        return this.getParticipantDataReqStatus;
    }

    public get getUserStatusRequestStatus() {
        return this.getUserStatusReqStatus;
    }

    public get setMeetingTypeRequestStatus() {
        return this.setMeetingTypeReqStatus;
    }

    public get getMeetingTypeDictionaryRequestStatus() {
        return this.getMeetingTypeDictionaryReqStatus;
    }

    public async getParticipantData(meetingId: string) {
        const fetchedParticipantData = await this.getParticipantDataReqStatus.handleAnyRequest(() =>
            this.service.getParticipantData(meetingId)
        );

        if (fetchedParticipantData) {
            this.participantData = new ParticipantDataFromResponse(fetchedParticipantData).meetingParticipant();
        }
    }

    public async getUserStatus(meetingId: string) {
        const fetchedUserStatus = await this.getUserStatusReqStatus.handleAnyRequest(() =>
            this.service.getUserStatus(meetingId)
        );

        if (fetchedUserStatus) {
            this.userStatus = new UserStatusFromResponse(fetchedUserStatus).userStatus();
        }
    }

    public async setMeetingType(meetingId: string, meetingType: MeetingType) {
        await this.setMeetingTypeReqStatus.handleAnyRequest(() => this.service.setMeetingType(meetingId, meetingType));
    }

    public async getMeetingTypeDictionary(language: string) {
        const fetchedDictionary = await this.getMeetingTypeDictionaryReqStatus.handleAnyRequest(() =>
            this.service.getMeetingTypeDictionary(language)
        );

        if (fetchedDictionary) {
            this.meetingTypeDictionary = new MeetingTypeDictionaryEntryFromResponse(
                fetchedDictionary
            ).dictionaryEntries();
        }
    }
}

export default new DefaultInMeetingAppStore(
    inMeetingAppService,
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus()
);
